import { SurveyDto, SurveyStatus } from '@/contracts/models/SurveyDto';
import { apiClient } from '@/services/clients/axiosClient';

export interface SurveyFeedbackSubmissionDto {
  responseId?: string;
  sessionId: string;
  response: Record<string, unknown>;
  isCompleted: boolean;
  completionTimeMs?: number;
  startTime?: string;
  endTime?: string;
  metadata?: Record<string, unknown>;
  lastAnsweredQuestionId?: string;
}

export interface SurveyFeedbackSubmissionResponse {
  responseId: string;
}

export interface SurveySummaryDto {
  surveyId: string;
  title: string;
  projects: string[];
  status: SurveyStatus;
  startDate: string | null;
  endDate: string | null;
  timezone: string | null;
  createdAt: string;
  mixerQuestionId: string | null;

  description?: string;
  estimatedMinutes?: number;
  responseCount: number;
  requireLogin: boolean;
}

export interface SurveyPublicSummaryDto {
  surveyId: string;
  title: string;
  description: string;
  estimatedMinutes: number;
  questionCount: number;
  mixerQuestionId?: string | null;
  isVotingEnabled: boolean;
  requireLogin: boolean;
}

export interface ExistingVote {
  id: string;
  questionId: string;
  answerText: string;
  isFavorite: boolean;
  createdAt: string;
  voteCount?: number;
  displayInitials?: string;
}

/**
 * Load public survey summary for landing page
 * @param surveyId - The ID of the survey to load
 * @returns Promise with survey summary or error
 */
export const LoadSurveySummary = async (
  surveyId: string
): Promise<{
  summary: SurveyPublicSummaryDto;
}> => {
  return await apiClient.get<{
    summary: SurveyPublicSummaryDto;
  }>(`/feedback/surveys/${surveyId}/summary`);
};

/**
 * Load a survey for taking
 * @param surveyId - The ID of the survey to load
 * @param sessionId - Optional session ID for anonymous user duplicate checking
 * @returns Promise with survey data or error
 */
export const LoadSurvey = async (
  surveyId: string,
  sessionId?: string
): Promise<{
  survey: SurveyDto;
  alreadyCompleted: boolean;
}> => {
  const params = sessionId ? `?sessionId=${sessionId}` : '';
  return await apiClient.get<{
    survey: SurveyDto;
    alreadyCompleted: boolean;
  }>(`/feedback/surveys/${surveyId}${params}`);
};

/**
 * Load surveys for a project
 * @param projectId - The ID of the project to load surveys for
 * @returns Promise with survey summaries or error
 */
export const LoadSurveysForProject = async (
  projectId: string
): Promise<{
  surveys: SurveySummaryDto[];
}> => {
  return await apiClient.get<{
    surveys: SurveySummaryDto[];
  }>(`/feedback/projects/${projectId}/surveys`);
};

export const LoadSurveyVotes = async (
  surveyId: string,
  sessionId?: string
): Promise<{
  existingVotes: Record<string, ExistingVote[]>;
  mixerQuestionText?: string | null;
  isVotingEnabled: boolean;
  mixerQuestionId?: string | null;
  surveyStatus?: SurveyStatus;
}> => {
  const params = sessionId ? `?sessionId=${sessionId}` : '';
  return await apiClient.get<{
    existingVotes: Record<string, ExistingVote[]>;
    mixerQuestionText?: string | null;
    isVotingEnabled: boolean;
    mixerQuestionId?: string | null;
    surveyStatus?: SurveyStatus;
  }>(`/feedback/surveys/${surveyId}/votes${params}`);
};

/**
 * Submit survey feedback
 * @param surveyId - The ID of the survey
 * @param feedbackData - The survey feedback data
 * @returns Promise with submission result or error
 */
export const submitSurveyFeedback = async (
  surveyId: string,
  feedbackData: SurveyFeedbackSubmissionDto
): Promise<SurveyFeedbackSubmissionResponse> => {
  return await apiClient.post<SurveyFeedbackSubmissionResponse>(
    `/feedback/surveys/${surveyId}/responses`,
    feedbackData
  );
};

export const submitVote = async (
  surveyId: string,
  questionId: string,
  answerId: string,
  answerText: string,
  sessionId: string
): Promise<void> => {
  const requestBody = {
    questionId,
    answerId,
    answerText,
    sessionId,
  };

  await apiClient.put(`/feedback/surveys/${surveyId}/votes`, requestBody);
};

/**
 * Calculate completion time from start and end times
 * @param startTime - When the survey was started
 * @param endTime - When the survey was completed
 * @returns Completion time in milliseconds
 */
export const calculateCompletionTime = (
  startTime: Date,
  endTime: Date
): number => {
  return endTime.getTime() - startTime.getTime();
};
